import { useState, type ReactNode } from 'react';
import { Dropdown } from '../dropdown';
import { Button, Field, Icon, Overlay, StatTabs } from '../ui';
import { AdminSelect } from './common';

export function AdministrativeFilters({
  label = 'Фильтры',
  fields,
  values,
  onApply,
  validate,
}: {
  label?: string;
  fields: (
    | {
        key: string;
        label: string;
        allLabel: string;
        options: { id: string; name: string }[];
        type?: 'select';
      }
    | {
        key: string;
        label: string;
        type: 'date';
        minKey?: string;
        maxKey?: string;
      }
  )[];
  values: Record<string, string>;
  onApply: (values: Record<string, string>) => void;
  validate?: (values: Record<string, string>) => string | null;
}) {
  const [draft, setDraft] = useState(values);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = fields.filter((field) => values[field.key]).length;
  return (
    <>
      <Button
        className={`icon-button admin-filter-button ${count ? 'has-filters' : ''}`}
        aria-label={label}
        aria-expanded={open}
        title={count ? `${label}: ${count}` : label}
        onClick={() => {
          setDraft(values);
          setError(null);
          setOpen(true);
        }}
      >
        <Icon name="Controls" size={16} />
        {count > 0 && <span className="admin-filter-dot" />}
      </Button>
      {open && (
        <Overlay title="Фильтры" onClose={() => setOpen(false)}>
          <div className="domain-form">
            {fields.map((field) =>
              field.type === 'date' ? (
                <Field
                  key={field.key}
                  label={field.label}
                  type="date"
                  value={draft[field.key] || ''}
                  min={field.minKey ? draft[field.minKey] || undefined : undefined}
                  max={field.maxKey ? draft[field.maxKey] || undefined : undefined}
                  onChange={(event) => {
                    setError(null);
                    setDraft((previous) => ({ ...previous, [field.key]: event.target.value }));
                  }}
                />
              ) : (
                <AdminSelect
                  key={field.key}
                  label={field.label}
                  value={draft[field.key] || ''}
                  emptyLabel={field.allLabel}
                  options={field.options}
                  onChange={(value) =>
                    setDraft((previous) => ({ ...previous, [field.key]: value }))
                  }
                />
              ),
            )}
            {error && (
              <p role="alert" className="admin-overdue">
                {error}
              </p>
            )}
          </div>
          <footer>
            <Button
              onClick={() => {
                setDraft({});
                setError(null);
              }}
            >
              Сбросить
            </Button>
            <Button
              className="primary"
              onClick={() => {
                const message = validate?.(draft);
                if (message) {
                  setError(message);
                  return;
                }
                onApply(draft);
                setOpen(false);
              }}
            >
              Применить
              {fields.some((field) => draft[field.key])
                ? ` (${fields.filter((field) => draft[field.key]).length})`
                : ''}
            </Button>
          </footer>
        </Overlay>
      )}
    </>
  );
}

/** List controls follow the archived People/Documents toolbar, including its compact menus. */
export function CompactFilter({
  label,
  allLabel,
  value = '',
  options,
  onChange,
}: {
  label: string;
  allLabel: string;
  value?: string;
  options: { id: string; name: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <Dropdown
      label={label}
      className={`admin-filter ${value ? 'selected' : ''}`}
      selected={[value]}
      options={[
        { id: '', label: allLabel },
        ...options.map((item) => ({ id: item.id, label: item.name })),
      ]}
      onChange={(values) => onChange(values[0] || '')}
    />
  );
}
export function AdministrativeList({
  count,
  toolbar,
  children,
  className = '',
  countLabel = 'Всего',
}: {
  count?: number | string;
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
  countLabel?: string;
}) {
  return (
    <section className={`panel data-panel admin-list ${className}`}>
      <StatTabs
        tabs={[{ value: 'all', label: countLabel, count: count ?? '…' }]}
        selected="all"
        onChange={() => {}}
      />
      {toolbar && <div className="table-toolbar">{toolbar}</div>}
      {children}
    </section>
  );
}
